<template>
  <div class="container">
    <div class="pull-up">
      <div class="filter-panel">
        <div class="filter-header">
          <i class="el-icon-filter filter-icon"></i>
          <span class="filter-title">{{ $t('supply.title.filters') }}</span>
        </div>
        <v-form>
          <v-row justify="center" align="center" class="filter-row">
            <v-col class="mb-0 filter-col">
              <v-select
                v-model="query.status"
                :items="[{ text: $t('supply.label.all_statuses'), value: null }, ...statuses.map(s => ({ text: s.name, value: s.value }))]"
                :label="$t('supply.label.status')"
                outlined dense clearable
                item-text="text" hide-details
                item-value="value"
              />
            </v-col>
            <v-col class="mb-0 filter-col">
              <v-select
                v-model="query.supplier"
                :items="[{ text: $t('supply.label.all_suppliers'), value: null }, ...suppliers.map(s => ({ text: s.name, value: s.id }))]"
                :label="$t('supply.label.supplier')"
                outlined dense clearable
                item-text="text" hide-details
                item-value="value"
              />
            </v-col>
            <v-col class="mb-0 filter-col">
              <v-select
                v-model="query.department"
                :items="[{ text: $t('supply.label.all_stores'), value: null }, ...departments.map(s => ({ text: s.name, value: s.id }))]"
                :label="$t('supply.label.store')"
                outlined dense clearable
                item-text="text" hide-details
                item-value="value"
              />
            </v-col>
            <v-col class="mb-0 filter-col">
              <v-select
                v-model="query.items"
                :items="[{ text: $t('supply.label.all_items'), value: null }, ...items.map(s => ({ text: `${s.code} - ${s.name}`, value: s.id }))]"
                :label="$t('supply.label.item')"
                outlined dense clearable multiple
                item-text="text" hide-details
                item-value="value"
              />
            </v-col>
            <v-col class="mb-0 filter-col">
              <v-menu
                ref="dateFromMenu"
                v-model="dateFromMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="query.date_from"
                    :label="$t('supply.label.date_from')"
                    prepend-inner-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    v-on="on" hide-details
                    outlined dense clearable
                  />
                </template>
                <v-date-picker
                  v-model="query.date_from"
                  @input="dateFromMenu = false"
                  no-title
                  scrollable
                />
              </v-menu>
            </v-col>
            <v-col class="mb-0 filter-col">
              <v-menu
                ref="dateToMenu"
                v-model="dateToMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="query.date_to"
                    :label="$t('supply.label.date_to')"
                    prepend-inner-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    v-on="on" hide-details
                    outlined dense clearable
                  />
                </template>
                <v-date-picker
                  v-model="query.date_to"
                  @input="dateToMenu = false"
                  no-title
                  scrollable
                />
              </v-menu>
            </v-col>
            <v-col class="filter-actions">
              <v-btn color="primary" @click="handleFilter" :loading="loading" :disabled="loading">
                <v-icon left>mdi-filter</v-icon>
                {{ $t('button.search') }}
              </v-btn>
              <v-btn v-if="hasActiveFilters" @click="clearFilters" :loading="loading" :disabled="loading" style="margin-left: 8px;">
                <v-icon left>mdi-filter-remove</v-icon>
                {{ $t('supply.button.clear_filters') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </div>
    </div>
      <div class="row">
        <div class="col-md-12">
          <div class="card m-b-30">
            <div class="card-body">
              <div class="table-responsive">
                <data-tables cell-class-name="text-center" :header-row-style="{textAlign: 'text-center'}"
                             :table-props="{border: true, stripe: true}" :pagination-props="{hideOnSinglePage: true}"
                             :data="supplies" v-loading="loading">
                  <el-table-column prop="code" :label="$t('label.ref')">
                    <template #default="{ row }">
                      <router-link :to="{ name: 'purchase-order', params: { id: row.id } }" class="po-link">
                        {{ row.code }}
                      </router-link>
                    </template>
                  </el-table-column>
                  <el-table-column align="center" show-overflow-tooltip prop="date" :formatter="$shortDate"
                                   :label="$t('label.date')">
                  </el-table-column>
                  <el-table-column align="center" show-overflow-tooltip prop="supplier.name"
                                   :label="$t('supply.label.supplier')"></el-table-column>
                  <el-table-column align="center" show-overflow-tooltip prop="department.name"
                                   :label="$t('supply.label.store')"></el-table-column>
                  <el-table-column align="center" prop="status" :label="$t('supply.label.status')" sortable width="130px">
                    <template #default="{row:item}">
                      <span :class="`text-${item.status_colour}`">{{ $t('supply.label.statuses.' + item.status) }}</span>
                      <!--                    <span class="text-black-50" v-else-if="item.is_pending">{{
                                              $t('supply.label.statuses.pending')
                                            }}</span>
                                          <span class="text-warning" v-if="item.is_partially_received">{{
                                              $t('supply.label.statuses.partially_received')
                                            }}</span>
                                          <span class="text-success" v-if="item.is_closed">{{ $t('supply.label.statuses.closed') }}</span>-->
                    </template>
                  </el-table-column>
                  <el-table-column align="center" prop="total" :label="$t('supply.title.total')" sortable>
                    <template #default="scope">
                      <span class="currency">{{ formatPrice(scope.row.total_cost) }}</span>
                    </template>
                  </el-table-column>
                  <!--<el-table-column align="center" prop="paid" :label="$t('supply.label.paid')" sortable>
                      <template #default="scope">
                        <span class="currency">{{ formatPrice(scope.row.paid) }}</span>
                      </template>
                  </el-table-column>
                  <el-table-column align="center" prop="owed" :label="$t('supply.label.owed')" sortable>
                    <template #default="scope">
                      <span class="currency">{{ formatPrice(scope.row.owed) }}</span>
                    </template>
                  </el-table-column>-->
                  <el-table-column align="center" show-overflow-tooltip prop="user.name"
                                   :label="$t('label.creator')"></el-table-column>
                  <el-table-column align="center" prop="items_count" :label="$t('supply.label.item_count')"
                                   sortable></el-table-column>
                  <el-table-column align="center" prop="received" :label="$t('supply.label.received')" sortable>
                    <template #default="{row}">
                      <el-progress  :percentage="percentageReceived(row)"></el-progress>
                    </template>
                  </el-table-column>
                  <el-table-column width="50px" align="center" fixed="right">
                    <template #header><i class="el-icon-setting"></i></template>
                    <template #default="{row, $index}">
                      <el-dropdown trigger="click">
                      <span class="el-dropdown-link">
                        <el-button type="text">
                          <i style="transform: rotate(90deg);" class="el-icon-more-outline el-icon--right"></i>
                        </el-button>
                      </span>
                        <el-dropdown-menu slot="dropdown">
                          <el-dropdown-item :disabled="row.is_draft" icon="el-icon-view"
                                            @click.native="showItemsDialog(row.id)">
                            {{ $t('supply.button.view_items') }}
                          </el-dropdown-item>
                          <el-dropdown-item v-show="row.is_draft || row.is_pending" icon="el-icon-edit"
                                            @click.native="editOrder(row)">
                            {{ $t('supply.button.edit_order') }}
                          </el-dropdown-item>
                          <el-dropdown-item v-if="row.is_draft" icon="el-icon-document-checked"
                                            @click.native="saveOrder(row, $index)">
                            {{ $t('supply.button.save_order') }}
                          </el-dropdown-item>
                          <el-dropdown-item v-else :disabled="row.is_closed" icon="el-icon-bottom"
                                            @click.native="receiveItems(row, $index)" class="bg-soft-warning">
                            {{ $t('supply.button.receive_order') }}
                          </el-dropdown-item>
                          <el-dropdown-item v-if="!row.is_closed && !row.is_draft" icon="el-icon-lock"
                                            @click.native="closeOrder(row, $index)">
                            {{ $t('supply.button.close_order') }}
                          </el-dropdown-item>
                          <el-dropdown-item :disabled="row.is_draft" icon="el-icon-printer"
                                            @click.native="downloadPurchaseOrderPDF(row)">
                            {{ $t('supply.button.print_order') }}
                          </el-dropdown-item>
                          <el-dropdown-item v-if="row.is_closed && row.received.length > 0" icon="el-icon-price-tag"
                                            @click.native="printLabels(row)">
                            {{ $t('supply.button.print_labels') }}
                          </el-dropdown-item>
                          <el-dropdown-item :disabled="row.is_draft" icon="el-icon-message"
                                            @click.native="emailPO(row)">
                            {{ $t('supply.button.email_supplier') }}
                          </el-dropdown-item>
                          <el-dropdown-item v-show="row.is_draft || row.is_pending"
                                            @click.native="deleteOrder(row, $index)"
                                            icon="el-icon-delete" class="text-danger">
                            {{ $t('supply.button.delete_order') }}
                          </el-dropdown-item>
                        </el-dropdown-menu>
                      </el-dropdown>
                      <!--<el-button type="primary" size="mini" @click.prevent="showItemsDialog(scope.row.id)">
                        <i class="mdi mdi-details"></i>
                      </el-button>
                      <el-button type="info" size="mini" icon="el-icon-printer"></el-button>-->
                    </template>
                  </el-table-column>
                </data-tables>
                <div class="pagination-center">
                  <pagination
                    :total="pagination.total"
                    :page-size="+pagination.per_page"
                    :page-sizes="[10, 20, 30, 40, 100]"
                    :current-page="+pagination.current_page"
                    @current-change="handlePageChange"
                    @size-change="handlePageSizeChange"
                    :loading="loading"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <supplies-items-modal :visible="itemsDialog" :items="poItems" @close="closeItemsDialog"></supplies-items-modal>
      <email-dialog :display="emailDialog" v-bind="poItem" @close="emailDialog = false"/>
      <receive-dialog :display="receiveDialog" :po="receivingPo" @close="receiveDialog = false"
                      @updated="handleFilter"/>
  </div>
</template>

<script>
import SuppliesItemsModal from '../Supplier/History/SuppliesItemsModal'
import EmailDialog from '@/components/Email/EmailDialog.vue'
import ReceiveDialog from '@/components/Purchase/Dialog/ReceiveDialog.vue'
import Pagination from '../_Partials/Pagination.vue'

export default {
  name: 'Supplies',
  components: {
    ReceiveDialog,
    EmailDialog,
    SuppliesItemsModal,
    Pagination
  },
  data () {
    return {
      emailDialog: false,
      receiveDialog: false,
      poItem: {},
      receivingPo: {},
      supplies: [],
      loading: false,
      statuses: [{
        name: this.$t('supply.label.statuses.draft'),
        value: 'draft',
        colour: 'danger'
      }, {
        name: this.$t('supply.label.statuses.partially_received'),
        value: 'partially_received',
        colour: 'warning'
      }, {
        name: this.$t('supply.label.statuses.pending'),
        value: 'pending',
        colour: 'black-50'
      }, {
        name: this.$t('supply.label.statuses.closed'),
        value: 'closed',
        colour: 'success'
      }],
      suppliers: [],
      departments: [],
      items: [],
      poItems: [],
      itemsDialog: false,
      search: '',
      query: {
        items: [],
        department: null,
        supplier: null,
        status: null,
        date_from: null,
        date_to: null
      },
      pagination: {
        current_page: 1,
        per_page: 10,
        total: 0,
        last_page: 1
      },
      dateFromMenu: false,
      dateToMenu: false
    }
  },
  methods: {
    async getPurchaseOrders (params = {}) {
      try {
        this.loading = true
        await this.withLoading(async () => {
          const mergedParams = {
            ...this.query,
            page: this.pagination.current_page,
            per_page: this.pagination.per_page,
            ...params
          }
          const data = await this.$supplyService.getSupplies(mergedParams)
          this.supplies = data.data
          this.pagination.current_page = data.meta.current_page
          this.pagination.per_page = data.meta.per_page
          this.pagination.total = data.meta.total
          this.pagination.last_page = data.meta.last_page
        }, 'loading')
      } catch (error) {
        // Error is already handled by the global interceptor
        console.log('Component caught error:', error)
      } finally {
        this.loading = false
      }
    },
    handlePageChange (page) {
      this.pagination.current_page = page
      this.getPurchaseOrders()
    },
    handlePageSizeChange (size) {
      this.pagination.per_page = size
      this.pagination.current_page = 1
      this.getPurchaseOrders()
    },
    handleFilter () {
      this.pagination.current_page = 1
      this.getPurchaseOrders()
    },
    clearFilters () {
      this.query = {
        items: [],
        department: null,
        supplier: null,
        status: null,
        date_from: null,
        date_to: null
      }
      this.pagination.current_page = 1
      this.getPurchaseOrders()
    },
    showItemsDialog (id) {
      const found = this.supplies.find(s => s.id === id)
      this.poItems = Array.isArray(found && found.items) ? found.items : []
      this.itemsDialog = true
    },
    receiveItems (po) {
      this.receivingPo = po
      this.receiveDialog = true
    },
    emailPO (po) {
      this.poItem = {
        po,
        title: this.$t('supply.title.email_po'),
        attachments: [{
          id: po.id,
          code: po.code,
          type: 'purchase_order'
        }],
        subject: this.$t('supply.title.email_default_subject', {
          department: po.department.name,
          code: po.code
        })
      }
      if (po.supplier.email) {
        this.poItem.to = [{
          name: po.supplier.name,
          email: po.supplier.email,
          photoType: 'icon'
        }]
      }
      this.emailDialog = true
    },
    closeItemsDialog (display) {
      this.itemsDialog = display
    },
    async deleteOrder (po, index) {
      this.askQuestion(this.$t('supply.confirm.delete'), async () => {
        try {
          await this.withLoading(async () => {
            await this.$supplyService.deleteOrder(po.id)
            this.showSuccess(this.$t('supply.message.deleted'))
            this.supplies.splice(index, 1)
          }, 'loading')
        } catch (error) {
          // Error is already handled by the global interceptor
          // Additional component-specific error handling can be added here if needed
          console.log('Component caught error:', error)
        }
      }, this.$t('status.warning'), () => {
      }, 'error')
    },
    async closeOrder (po, index) {
      this.askQuestion(this.$t('supply.confirm.close', { msg: (po.received.length ? this.$t('supply.confirm.unreceived_items') : '') }),
        async () => {
          try {
            await this.withLoading(async () => {
              const data = await this.$supplyService.closeOrder(po.id)
              this.showSuccess(this.$t('supply.message.closed'))
              this.supplies.splice(index, 1, data)
            }, 'loading')
          } catch (error) {
            // Error is already handled by the global interceptor
            console.log('Component caught error:', error)
          }
        }, this.$t('status.warning'), () => {
        }, 'warning', {
          confirmButtonText: this.$t('button.close'),
          confirmButtonClass: 'el-button--warning'
        })
    },
    async saveOrder (po, index) {
      this.askQuestion(this.$t('supply.confirm.save'), async () => {
        try {
          await this.withLoading(async () => {
            const data = await this.$supplyService.saveOrder(po.id)
            this.showSuccess(this.$t('supply.message.saved'))
            this.supplies.splice(index, 1, data)
          }, 'loading')
        } catch (error) {
          // Error is already handled by the global interceptor
          console.log('Component caught error:', error)
        }
      })
    },
    percentageReceived (row) {
      const receivedQuantity = row.received.map(rc => rc.items).flat(1).map(rc => rc.quantity).reduce((a, b) => a + b, 0)
      const totalQuantity = row.items.map(it => it.quantity).reduce((a, b) => a + b, 0)

      if (!totalQuantity || isNaN(totalQuantity) || totalQuantity === 0) {
        return 0
      }

      const percentage = Math.floor((receivedQuantity / totalQuantity) * 100)

      // Ensure the percentage is between 0 and 100
      return Math.max(0, Math.min(100, percentage))
    },
    editOrder (po) {
      this.$router.push({
        name: 'add-supply',
        query: {
          id: po.id,
          edit: true
        }
      })
    },
    printLabels (po) {
      this.$router.push({
        name: 'labels',
        query: {
          po_id: po.id
        }
      })
    },
    async downloadPurchaseOrderPDF (po) {
      if (!po || !po.id) {
        this.$message.error(this.$t('supply.error.invalid_order'))
        return
      }

      try {
        await this.withLoading(async () => {
          const response = await this.$supplyService.downloadPurchaseOrderPDF(po.id)

          // Extract filename from response headers or use default
          const contentDisposition = response.headers['content-disposition']
          let filename = `purchase_order_${po.code || po.id}_${new Date().toISOString().split('T')[0]}.pdf`

          if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
            if (filenameMatch && filenameMatch[1]) {
              filename = filenameMatch[1].replace(/['"]/g, '')
              // Decode URI if needed
              try {
                filename = decodeURIComponent(filename)
              } catch (e) {
                // If decoding fails, use as is
              }
            }
          }

          // Create download link
          const blob = new Blob([response.data], {
            type: 'application/pdf'
          })
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.download = filename
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)

          this.showSuccess(this.$t('supply.message.download_success'))
        }, 'loading')
      } catch (error) {
        // Error is already handled by the global interceptor
        console.log('Component caught error in downloadPurchaseOrderPDF:', error)
        this.$message.error(this.$t('supply.error.download_failed'))
      }
    }
  },
  computed: {
    hasActiveFilters () {
      return (
        this.query.status !== null ||
        this.query.supplier !== null ||
        this.query.department !== null ||
        (Array.isArray(this.query.items) && this.query.items.length > 0) ||
        this.query.date_from !== null ||
        this.query.date_to !== null
      )
    }
  },
  async mounted () {
    this.$emit('parent-title', this.$t('supply.title.purchase'))

    try {
      await this.withLoading(async () => {
        await this.getPurchaseOrders()
      }, 'loading')
    } catch (error) {
      // Error is already handled by the global interceptor
      console.log('Component caught error in getPurchaseOrders:', error)
    }

    try {
      await this.withLoading(async () => {
        this.suppliers = await this.$supplierService.getSuppliers()
      }, 'loading')
    } catch (error) {
      // Error is already handled by the global interceptor
      console.log('Component caught error in getSuppliers:', error)
    }

    try {
      await this.withLoading(async () => {
        this.items = await this.$itemService.getItems()
      }, 'loading')
    } catch (error) {
      // Error is already handled by the global interceptor
      console.log('Component caught error in getItems:', error)
    }

    try {
      await this.withLoading(async () => {
        this.departments = await this.$departmentService.getDepartmentsWithWarehouse()
      }, 'loading')
    } catch (error) {
      // Error is already handled by the global interceptor
      console.log('Component caught error in getDepartmentsWithWarehouse:', error)
    }
  }
}
</script>

<style scoped>
.pagination-center {
  display: flex;
  justify-content: center;
  margin-top: 16px;
}
.filter-panel {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
  padding: 24px 24px 12px 24px;
  margin-bottom: 24px;
  border: 1px solid #f0f0f0;
}
.filter-header {
  display: flex;
  align-items: center;
  margin-bottom: 18px;
}
.filter-icon {
  font-size: 22px;
  color: #409EFF;
  margin-right: 8px;
}
.filter-title {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}
.filter-row {
  margin-bottom: 0;
}
.filter-col {
  min-width: 180px;
  max-width: 260px;
}
.filter-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
